const HTTP_STATUS_UNAUTHORIZED = 401
const HTTP_STATUS_FORBIDDEN = 403
const DEFAULT_CREDENTIALS: RequestCredentials = "include"
const ABORT_ERROR_NAME = "AbortError"
const CSRF_TOKEN_HEADER = "X-CSRF-Token"
const CSRF_TOKEN_RESPONSE_KEY = "csrfToken"
const CSRF_ERROR_CODE = "CSRF_INVALID"
const RESPONSE_CODE_KEY = "code"
const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"])

type UnauthorizedHandler = () => void | Promise<void>

let unauthorizedHandler: UnauthorizedHandler | null = null
let isHandlingUnauthorized = false
let csrfToken: string | null = null
let csrfTokenPromise: Promise<string | null> | null = null
let csrfTokenEndpoint: string | null = null

export function setUnauthorizedHandler(handler: UnauthorizedHandler) {
  unauthorizedHandler = handler
}

export function clearUnauthorizedHandler() {
  unauthorizedHandler = null
}

export function clearCsrfToken() {
  // CSRF関連の状態をすべてクリアする
  csrfToken = null
  csrfTokenPromise = null
  csrfTokenEndpoint = null
}

export async function initCsrfToken(endpoint: string, signal?: AbortSignal) {
  if (endpoint === null || endpoint === undefined || endpoint === "") {
    throw new Error("CSRFトークン取得先が未指定です。")
  }

  csrfTokenEndpoint = endpoint

  if (csrfTokenPromise) {
    return csrfTokenPromise
  }

  csrfTokenPromise = (async () => {
    try {
      const response = await fetch(endpoint, {
        method: "GET",
        credentials: DEFAULT_CREDENTIALS,
        signal
      })

      if (!response.ok) {
        throw new Error("CSRFトークン取得に失敗しました。")
      }

      const data = (await response.json()) as { [CSRF_TOKEN_RESPONSE_KEY]?: unknown }
      const token = data[CSRF_TOKEN_RESPONSE_KEY]
      if (typeof token !== "string" || token.length === 0) {
        throw new Error("CSRFトークンの形式が不正です。")
      }

      csrfToken = token
      return csrfToken
    } catch (error) {
      const errorName = error instanceof Error ? error.name : ""
      if (errorName === ABORT_ERROR_NAME) {
        throw error
      }
      console.error("CSRFトークン取得に失敗しました。", error)
      throw error
    } finally {
      csrfTokenPromise = null
    }
  })()

  return csrfTokenPromise
}

async function isCsrfInvalidResponse(response: Response) {
  // CSRFエラーコードを含むレスポンスかどうかを判定する
  try {
    const contentType = response.headers.get("content-type") ?? ""
    if (!contentType.includes("application/json")) {
      return false
    }
    const data = (await response.clone().json()) as { [RESPONSE_CODE_KEY]?: unknown }
    return data[RESPONSE_CODE_KEY] === CSRF_ERROR_CODE
  } catch (error) {
    console.error("CSRF判定用レスポンスの解析に失敗しました。", error)
    return false
  }
}

async function disposeResponseBody(response: Response) {
  // リトライ前にレスポンスボディを解放する
  try {
    const body = response.body
    if (body) {
      await body.cancel()
      return
    }
  } catch (error) {
    console.error("レスポンスボディの破棄に失敗しました。", error)
  }

  try {
    await response.text()
  } catch (error) {
    console.error("レスポンスボディの読み取りに失敗しました。", error)
  }
}

export const clientFetch = async (input: RequestInfo | URL, init: RequestInit = {}) => {
  // 共通ヘッダとCSRFを考慮したfetchラッパー
  if (input === null || input === undefined) {
    throw new Error("リクエスト先が未指定です。")
  }

  const method = (init.method ?? "GET").toUpperCase()
  const headers = new Headers(init.headers)

  if (csrfToken && MUTATING_METHODS.has(method)) {
    headers.set(CSRF_TOKEN_HEADER, csrfToken)
  }

  try {
    // 実際の通信処理を担う内部関数（CSRFリトライ制御をここで完結させる）
    const executeFetch = async (isRetryingForCsrf: boolean, requestHeaders: Headers): Promise<Response> => {
      // CSRF再試行は1回のみのため、リトライ中は判定をスキップする
      const response = await fetch(input, {
        ...init,
        headers: requestHeaders,
        credentials: DEFAULT_CREDENTIALS
      })

      const isForbidden = response.status === HTTP_STATUS_FORBIDDEN
      const endpoint = csrfTokenEndpoint
      // CSRF再試行は1回のみ許可する（リトライ中は判定をスキップ）
      const shouldRetryForCsrf = !isRetryingForCsrf && isForbidden && MUTATING_METHODS.has(method) && endpoint && await isCsrfInvalidResponse(response)
      if (shouldRetryForCsrf) {
        await disposeResponseBody(response)
        try {
          await initCsrfToken(endpoint)
        } catch (error) {
          console.error("CSRFトークンの再取得に失敗しました。", error)
          return response
        }
        const retryHeaders = new Headers(init.headers)
        if (csrfToken) {
          retryHeaders.set(CSRF_TOKEN_HEADER, csrfToken)
        }
        return executeFetch(true, retryHeaders)
      }

      if (response.status === HTTP_STATUS_UNAUTHORIZED && unauthorizedHandler && !isHandlingUnauthorized) {
        isHandlingUnauthorized = true
        try {
          await unauthorizedHandler()
        } catch (error) {
          console.error("認証エラーハンドリングに失敗しました。", error)
        } finally {
          isHandlingUnauthorized = false
        }
      }

      return response
    }

    return executeFetch(false, headers)
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw error
    }
    console.error("通信に失敗しました。", error)
    throw error
  }
}
